/**
 * LifecycleDesignerView — orchestrator del Designer cuando el template
 * es de tipo `lifecycle` (B.6.d.9.g).
 *
 * Layout paralelo al `DesignerScreen` del artifact, pero con el modelo
 * lifecycle:
 *   - Header con metadata del template + tabs (Designer / Vista del usuario).
 *   - Banner global con name + description (per locale) — versión simple,
 *     sin AI config (lifecycle no genera contenido por IA, es estructural).
 *   - Designer tab → 2 columnas (LifecycleStructureTree + LifecyclePhaseEditor)
 *     y modal de picker de artifact al añadir.
 *   - User view tab → LifecyclePreview full-width.
 *   - Autosave: `useDefinitionAutosave`, la regla común de los editores.
 */

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'

import type {
  LifecycleDefinition,
  Template,
  TemplateVersionWithDefinition,
} from '../../../api/templates'
import { DesignerTabs, type DesignerTab } from './DesignerTabs'
import { LifecycleArtifactPickerDialog } from './LifecycleArtifactPickerDialog'
import { LifecyclePhaseEditor } from './LifecyclePhaseEditor'
import { LifecyclePreview } from './LifecyclePreview'
import { LifecycleStructureTree } from './LifecycleStructureTree'
import { VersionsTab } from './VersionsTab'
import { useLifecycleDesignerState } from './lifecycleState'
import { useDefinitionAutosave, type AutosaveState } from './useDefinitionAutosave'
import { editingLocale, SUPPORTED_LOCALES, type SupportedLocale } from '../../../i18n/config'
import type { TenantManageBlock } from '../../../lib/tiers'

interface LifecycleDesignerViewProps {
  template: Template
  version: TemplateVersionWithDefinition
  /** OT-0009 N2 — calculado una vez en `TemplateDetailView` (`useMe`),
   * no se vuelve a resolver aquí. `updateVersionDefinition` exige
   * `require_tenant_admin` en backend y una organización que permita
   * escribir (`tenantManageBlock`, OT-0073 §4.4); `null` = puede editar. */
  manageBlock: TenantManageBlock | null
  /** Header (con metadata + estado del autosave) lo monta el padre. Aquí
   * solo recibimos el render-prop para insertarlo arriba. */
  renderHeader: (autosave: AutosaveState) => React.ReactNode
  /** Read-only banner igualmente delegado al padre. */
  renderReadOnlyBanner: () => React.ReactNode
}

export function LifecycleDesignerView({
  template,
  version,
  manageBlock,
  renderHeader,
  renderReadOnlyBanner,
}: LifecycleDesignerViewProps) {
  const { i18n } = useTranslation('templates')
  const [activeTab, setActiveTab] = useState<DesignerTab>('designer')

  // OT-0009 N2 — mismo defecto que N1 en el designer de artifacts:
  // sin el bloqueo por tier, cualquier tier podía editar la estructura del
  // lifecycle y el autosave fallaba en silencio con 403.
  const readOnly =
    template.is_system_default || version.published_at !== null || manageBlock !== null

  const initialDefinition = useMemo<LifecycleDefinition>(
    () => version.definition as unknown as LifecycleDefinition,
    [version.definition],
  )

  const [state, dispatch] = useLifecycleDesignerState(
    initialDefinition,
    editingLocale(Object.keys(initialDefinition.i18n), i18n.language),
  )

  // Re-hidratación cuando la versión remota cambia.
  useEffect(() => {
    dispatch({ type: 'SET_DEFINITION', definition: initialDefinition })
  }, [initialDefinition, dispatch])

  const autosave = useDefinitionAutosave({
    templateId: template.id,
    versionId: version.id,
    definition: state.definition,
    savedDefinition: initialDefinition,
    readOnly,
    onSaved: () => dispatch({ type: 'MARK_CLEAN' }),
  })

  // ─── Picker modal ──────────────────────────────────────────────────────
  const [pickerState, setPickerState] = useState<{
    open: boolean
    phaseCode: string | null
  }>({ open: false, phaseCode: null })

  function openDeliverablePicker(phaseCode: string) {
    setPickerState({ open: true, phaseCode })
  }
  function closeDeliverablePicker() {
    setPickerState({ open: false, phaseCode: null })
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      {renderHeader(autosave)}
      <DesignerTabs active={activeTab} onChange={setActiveTab} />

      {readOnly ? renderReadOnlyBanner() : null}

      <LifecycleGlobalConfigBanner
        state={state}
        dispatch={dispatch}
        readOnly={readOnly}
      />

      {activeTab === 'designer' ? (
        <div className="flex-1 min-h-0 grid grid-cols-[360px_minmax(0,1fr)] divide-x divide-line">
          <LifecycleStructureTree
            state={state}
            dispatch={dispatch}
            readOnly={readOnly}
            onAddDeliverableRequest={openDeliverablePicker}
          />
          <div className="min-w-0 overflow-hidden">
            <LifecyclePhaseEditor
              state={state}
              dispatch={dispatch}
              readOnly={readOnly}
              methodologyId={template.methodology_id}
            />
          </div>
        </div>
      ) : activeTab === 'user_view' ? (
        <div className="flex-1 min-h-0 overflow-y-auto bg-canvas">
          <div className="max-w-4xl mx-auto py-6 px-4">
            <LifecyclePreview state={state} />
          </div>
        </div>
      ) : activeTab === 'versions' ? (
        <div className="flex-1 min-h-0 overflow-y-auto bg-canvas">
          <div className="max-w-3xl mx-auto py-6 px-4">
            <VersionsTab template={template} />
          </div>
        </div>
      ) : null}

      <LifecycleArtifactPickerDialog
        open={pickerState.open}
        state={state}
        dispatch={dispatch}
        phaseCode={pickerState.phaseCode}
        methodologyId={template.methodology_id}
        onClose={closeDeliverablePicker}
      />
    </div>
  )
}

// ─── Banner global simple del lifecycle ───────────────────────────────────
function LifecycleGlobalConfigBanner({
  state,
  dispatch,
  readOnly,
}: {
  state: ReturnType<typeof useLifecycleDesignerState>[0]
  dispatch: ReturnType<typeof useLifecycleDesignerState>[1]
  readOnly: boolean
}) {
  const { t } = useTranslation('templates')
  const block = state.definition.i18n[state.activeLocale]
  const allLocales = SUPPORTED_LOCALES
  const activeLocales = Object.keys(state.definition.i18n) as SupportedLocale[]
  const missingLocales = allLocales.filter((l) => !activeLocales.includes(l))

  return (
    <section className="bg-surface border-b border-line">
      <header className="px-6 py-3 flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
            {t('designer.lifecycle.global_eyebrow')}
          </p>
          <p className="text-[11px] text-ink-subtle italic">
            {t('designer.lifecycle.global_hint')}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wide text-ink-subtle">
            {t('designer.global_config.editing_locale')}
          </span>
          {activeLocales.map((loc) => (
            <button
              key={loc}
              type="button"
              onClick={() => dispatch({ type: 'SET_LOCALE', locale: loc })}
              className={
                loc === state.activeLocale
                  ? 'px-2 py-0.5 text-xs font-mono uppercase rounded bg-brand-soft text-brand'
                  : 'px-2 py-0.5 text-xs font-mono uppercase rounded hover:bg-surface-muted text-ink-muted'
              }
            >
              {loc}
            </button>
          ))}
          {!readOnly && missingLocales.length > 0
            ? missingLocales.map((loc) => (
                <button
                  key={loc}
                  type="button"
                  onClick={() => dispatch({ type: 'ADD_LOCALE', locale: loc })}
                  title={t('designer.global_config.add_locale')}
                  className="px-2 py-0.5 text-xs font-mono uppercase rounded border border-dashed border-line text-ink-subtle hover:text-ink hover:border-line-strong inline-flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  {loc}
                </button>
              ))
            : null}
        </div>
      </header>

      <div className="px-6 pb-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wide font-medium text-ink-subtle">
            {t('designer.lifecycle.global_name_label')}
          </span>
          <input
            type="text"
            value={block?.name ?? ''}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_GLOBAL_I18N',
                locale: state.activeLocale,
                patch: { name: e.target.value },
              })
            }
            disabled={readOnly}
            className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-brand disabled:opacity-60"
          />
        </label>

        <label className="block">
          <span className="text-[10px] uppercase tracking-wide font-medium text-ink-subtle">
            {t('designer.lifecycle.global_description_label')}
          </span>
          <input
            type="text"
            value={block?.description ?? ''}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_GLOBAL_I18N',
                locale: state.activeLocale,
                patch: { description: e.target.value },
              })
            }
            disabled={readOnly}
            className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-brand disabled:opacity-60"
          />
        </label>
      </div>
    </section>
  )
}
